/* eslint-disable no-param-reassign */
import UnicodeRegular from 'asset/font/DejaVuSans-Regular';
import UnicodeBold from 'asset/font/DejaVuSans-Bold';
import type { Undo } from 'adapter/in/reports/utils/generatePDF';
import { processEmoji, rgbaToRgb } from 'adapter/in/reports/utils/reportRendering';

/**
 * Injects DejaVu Sans font-face CSS into the document if not already present.
 */
export const ensureTextBlockFontInjected = () => {
    const id = '__dejavu_sans_injected__';
    if (document.getElementById(id)) return;

    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
    @font-face {
        font-family: 'dejavusans';
        src: url(data:font/ttf;base64,${UnicodeRegular}) format('truetype');
        font-weight: 400;
        font-style: normal;
        font-display: swap;
    }
    @font-face {
        font-family: 'dejavusans';
        src: url(data:font/ttf;base64,${UnicodeBold}) format('truetype');
        font-weight: 700;
        font-style: normal;
        font-display: swap;
    }
`;
    document.head.appendChild(style);
};

/**
 * Loads the DejaVu Sans font into the browser font cache once.
 */
export const loadTextBlockFontOnce = async () => {
    const anyDoc = document as any;
    if (anyDoc.fonts && typeof anyDoc.fonts.load === 'function') {
        try {
            await Promise.all([anyDoc.fonts.load('400 12px dejavusans'), anyDoc.fonts.load('700 12px dejavusans')]);
        } catch {
            console.log('Failed to load DejaVu fonts');
        }
    }
};

/**
 * Converts all rgba background colors in the element's subtree to rgb, and processes emoji in text nodes.
 * Rgba colors can have issues in PDF rendering, i.e. with background colors.
 */
export const rgbaToRgbString = (element: HTMLElement) => {
    element.querySelectorAll('*').forEach((el) => {
        const cs = window.getComputedStyle(el as Element);
        const bg = cs.backgroundColor;
        if (bg.includes('rgba') && !bg.includes('0, 0, 0, 0)')) {
            (el as HTMLElement).style.backgroundColor = rgbaToRgb(bg);
        }
        if (el.childNodes?.length) {
            Array.from(el.childNodes)
                .filter((n) => n.nodeType === Node.TEXT_NODE)
                .forEach((n) => processEmoji.processTextNode(n));
        }
    });
};

/**
 * Normalizes list styles in the element's subtree for PDF rendering, and stores undo functions.
 * This is needed because html2canvas does not handle Tiptap list styles well.
 */
export const normalizeTextBlockLists = (element: HTMLElement, listUndos: Undo[]) => {
    element.querySelectorAll('ul, ol').forEach((listNode) => {
        const list = listNode as HTMLElement;
        const isOrdered = list.tagName.toLowerCase() === 'ol';

        const prevListStyle = {
            listStyle: list.style.listStyle,
            paddingLeft: list.style.paddingLeft,
            marginLeft: list.style.marginLeft,
        };
        list.style.listStyle = 'none';
        list.style.paddingLeft = '0';
        list.style.marginLeft = '0';
        listUndos.push(() => {
            list.style.listStyle = prevListStyle.listStyle;
            list.style.paddingLeft = prevListStyle.paddingLeft;
            list.style.marginLeft = prevListStyle.marginLeft;
        });

        Array.from(list.children)
            .filter((n): n is HTMLElement => n.tagName?.toLowerCase() === 'li')
            .forEach((_li, idx) => {
                const li = _li as HTMLElement;
                const originalChildren = Array.from(li.childNodes);

                const prevLi = {
                    display: li.style.display,
                    gap: li.style.gap,
                    alignItems: li.style.alignItems,
                    margin: li.style.margin,
                };

                li.style.display = 'flex';
                li.style.alignItems = 'baseline';
                li.style.gap = '0.4em';
                if (!li.style.margin) li.style.margin = '0 0 8px 0';

                const marker = document.createElement('span');
                marker.textContent = isOrdered ? `${idx + 1}.` : '•';
                marker.style.minWidth = isOrdered ? '1.4em' : '1.0em';
                marker.style.textAlign = 'right';
                marker.style.flex = '0 0 auto';

                const content = document.createElement('span');
                content.style.flex = '1 1 auto';
                content.style.display = 'inline';
                originalChildren.forEach((ch) => content.appendChild(ch));

                li.appendChild(marker);
                li.appendChild(content);

                listUndos.push(() => {
                    li.style.display = prevLi.display;
                    li.style.alignItems = prevLi.alignItems;
                    li.style.gap = prevLi.gap;
                    li.style.margin = prevLi.margin;

                    if (marker.parentNode === li) li.removeChild(marker);
                    if (content.parentNode === li) li.removeChild(content);

                    originalChildren.forEach((ch) => li.appendChild(ch));
                });
            });
    });
};

const getComputedBorder = (computedStyle: CSSStyleDeclaration, side: 'top' | 'right' | 'bottom' | 'left'): string => {
    const borderWidth = computedStyle.getPropertyValue(`border-${side}-width`);
    const borderStyle = computedStyle.getPropertyValue(`border-${side}-style`);
    const borderColor = computedStyle.getPropertyValue(`border-${side}-color`);

    if (!borderWidth || Number.parseFloat(borderWidth) === 0 || borderStyle === 'none' || borderStyle === 'hidden') {
        return '0';
    }

    return `${borderWidth} ${borderStyle || 'solid'} ${borderColor || 'currentColor'}`;
};

const getFirstVisibleBorder = (...borders: Array<string | undefined>): string =>
    borders.find((border) => border !== undefined && border !== '0') ?? '0';

/**
 * Rewrites collapsed table borders into non-overlapping borders for html2canvas.
 * Adjacent collapsed cell borders can be painted twice in the generated PDF.
 */
export const normalizeTextBlockTables = (element: HTMLElement, tableUndos: Undo[]) => {
    element.querySelectorAll('table').forEach((tableNode) => {
        const table = tableNode as HTMLTableElement;
        const rows = Array.from(table.rows);
        const rowCells = rows.map((row) => Array.from(row.cells));
        const cellBorders = rowCells.map((cells) =>
            cells.map((cell) => {
                const computedStyle = window.getComputedStyle(cell);

                return {
                    top: getComputedBorder(computedStyle, 'top'),
                    right: getComputedBorder(computedStyle, 'right'),
                    bottom: getComputedBorder(computedStyle, 'bottom'),
                    left: getComputedBorder(computedStyle, 'left'),
                };
            })
        );

        const previousTableStyle = {
            borderCollapse: table.style.borderCollapse,
            borderSpacing: table.style.borderSpacing,
        };

        table.style.borderCollapse = 'separate';
        table.style.borderSpacing = '0';

        tableUndos.push(() => {
            table.style.borderCollapse = previousTableStyle.borderCollapse;
            table.style.borderSpacing = previousTableStyle.borderSpacing;
        });

        rows.forEach((row, rowIndex) => {
            const cells = rowCells[rowIndex];
            const isLastRow = rowIndex === rows.length - 1;

            cells.forEach((cell, cellIndex) => {
                const isLastCell = cellIndex === cells.length - 1;
                const borders = cellBorders[rowIndex][cellIndex];
                const previousRowBorders = cellBorders[rowIndex - 1]?.[cellIndex];
                const previousCellBorders = cellBorders[rowIndex][cellIndex - 1];
                const previousCellStyle = {
                    border: cell.style.border,
                    borderTop: cell.style.borderTop,
                    borderRight: cell.style.borderRight,
                    borderBottom: cell.style.borderBottom,
                    borderLeft: cell.style.borderLeft,
                };

                cell.style.border = '0';
                cell.style.borderTop = rowIndex === 0 ? borders.top : getFirstVisibleBorder(borders.top, previousRowBorders?.bottom);
                cell.style.borderLeft =
                    cellIndex === 0 ? borders.left : getFirstVisibleBorder(borders.left, previousCellBorders?.right);
                cell.style.borderRight = isLastCell ? borders.right : '0';
                cell.style.borderBottom = isLastRow ? borders.bottom : '0';

                tableUndos.push(() => {
                    cell.style.border = previousCellStyle.border;
                    cell.style.borderTop = previousCellStyle.borderTop;
                    cell.style.borderRight = previousCellStyle.borderRight;
                    cell.style.borderBottom = previousCellStyle.borderBottom;
                    cell.style.borderLeft = previousCellStyle.borderLeft;
                });
            });
        });
    });
};

/**
 * Reverts the list normalization changes by executing stored undo functions.
 * We need to reverse the order of undos to restore the original state correctly,
 * ensuring the browser DOM remains exactly as it was before normalization.
 */
export const undoNormalizeTextBlockLists = (listUndos: Undo[]) => {
    listUndos.reverse().forEach((undo) => undo());
};

export const undoNormalizeTextBlockTables = (tableUndos: Undo[]) => {
    tableUndos.reverse().forEach((undo) => undo());
};
